import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { useAuth } from './useAuth';
import { analyticsService } from '../services/analyticsService';
import type { AnalyticsEventParams } from '../types/analytics';
import {
  createCheckoutSession,
  quickPurchase,
} from '../transport/api/protectedApi';
import { setPurchaseIntent } from '../utilities/purchaseIntent';
import { createLogger } from '@airhead-games/shared/utilities';
import type { ProductDocument } from '@airhead-games/shared/types';

const logger = createLogger('useProductCheckout');

type CheckoutFlowType =
  | 'quick_purchase'
  | 'stripe_checkout'
  | 'quick_purchase_fallback'
  | 'quick_purchase_exception';

interface ProductCheckoutParams {
  product: ProductDocument;
  trackingLabel: string;
  trackingContext?: Partial<AnalyticsEventParams>;
  savedPaymentMethodId?: string | null;
  cancelPath?: string;
  onQuickPurchaseSuccess?: () => void;
  quickPurchaseSuccessDurationMs?: number;
}

type ProductCheckoutResult =
  'succeeded' | 'redirected' | 'login_redirect' | 'error';

interface UseProductCheckoutResult {
  checkoutProduct: (
    params: ProductCheckoutParams
  ) => Promise<ProductCheckoutResult>;
  activeCheckoutPriceId: string | null;
  purchaseSuccessPriceId: string | null;
  checkoutError: string | null;
  clearCheckoutError: () => void;
}

interface RedirectToCheckoutParams {
  stripePriceId: string;
  cancelPath: string;
  analyticsParams: AnalyticsEventParams;
  flowType: CheckoutFlowType;
}

const createCheckoutAttemptId = (): string =>
  `checkout_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }

  return 'Unknown error';
};

const buildCheckoutAnalyticsParams = ({
  checkoutAttemptId,
  product,
  hasSavedMethod,
  isAuthenticated,
  trackingContext,
}: {
  checkoutAttemptId: string;
  product: ProductDocument;
  hasSavedMethod: boolean;
  isAuthenticated: boolean;
  trackingContext?: Partial<AnalyticsEventParams>;
}): AnalyticsEventParams => ({
  checkout_attempt_id: checkoutAttemptId,
  stripe_price_id: product.stripePriceId,
  product_name: product.name,
  loons_granted: product.loonsGranted ?? undefined,
  list_price: product.listPrice,
  currency: product.currency,
  product_interval: product.interval || undefined,
  has_saved_method: hasSavedMethod,
  is_authenticated: isAuthenticated,
  ...trackingContext,
});

export function useProductCheckout(): UseProductCheckoutResult {
  const { user, refetchUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeCheckoutPriceId, setActiveCheckoutPriceId] = useState<
    string | null
  >(null);
  const [purchaseSuccessPriceId, setPurchaseSuccessPriceId] = useState<
    string | null
  >(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const successResetTimerRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (successResetTimerRef.current !== null) {
        window.clearTimeout(successResetTimerRef.current);
      }
    },
    []
  );

  const clearCheckoutError = useCallback(() => {
    setCheckoutError(null);
  }, []);

  const redirectToCheckout = useCallback(
    async ({
      stripePriceId,
      cancelPath,
      analyticsParams,
      flowType,
    }: RedirectToCheckoutParams): Promise<ProductCheckoutResult> => {
      try {
        const response = await createCheckoutSession(stripePriceId, cancelPath);

        if (response.success && response.data?.url) {
          analyticsService.trackCheckoutRedirected({
            ...analyticsParams,
            flow_type: flowType,
            checkout_result: 'redirected',
            navigation_target: response.data.url,
            cancel_path: cancelPath,
          });
          window.location.href = response.data.url;
          return 'redirected';
        }

        analyticsService.trackCheckoutFailed({
          ...analyticsParams,
          flow_type: flowType,
          checkout_result: 'error',
          error_code: response.success
            ? 'checkout_session_missing_url'
            : 'checkout_session_request_failed',
          error_message: response.success
            ? 'Checkout session missing URL'
            : 'Checkout session request failed',
        });
        setCheckoutError('Something went wrong. Please try again.');
        setActiveCheckoutPriceId(null);
        return 'error';
      } catch (checkoutErrorValue) {
        logger.error('Failed to create checkout session', checkoutErrorValue);
        analyticsService.trackCheckoutFailed({
          ...analyticsParams,
          flow_type: flowType,
          checkout_result: 'error',
          error_code: 'checkout_session_exception',
          error_message: getErrorMessage(checkoutErrorValue),
        });
        setCheckoutError('Something went wrong. Please try again.');
        setActiveCheckoutPriceId(null);
        return 'error';
      }
    },
    []
  );

  const checkoutProduct = useCallback(
    async ({
      product,
      trackingLabel,
      trackingContext,
      savedPaymentMethodId,
      cancelPath,
      onQuickPurchaseSuccess,
      quickPurchaseSuccessDurationMs = 2000,
    }: ProductCheckoutParams): Promise<ProductCheckoutResult> => {
      const checkoutAttemptId = createCheckoutAttemptId();
      const baseAnalyticsParams = buildCheckoutAnalyticsParams({
        checkoutAttemptId,
        product,
        hasSavedMethod: !!savedPaymentMethodId,
        isAuthenticated: !!user,
        trackingContext,
      });

      analyticsService.trackButtonClick(trackingLabel, {
        ...baseAnalyticsParams,
      });
      analyticsService.trackCheckoutStarted({
        ...baseAnalyticsParams,
        flow_type: savedPaymentMethodId ? 'quick_purchase' : 'stripe_checkout',
      });

      if (!user) {
        setPurchaseIntent({
          stripePriceId: product.stripePriceId,
          checkoutAttemptId,
        });
        analyticsService.trackCheckoutLoginRedirect({
          ...baseAnalyticsParams,
          flow_type: 'stripe_checkout',
          checkout_result: 'login_redirect',
          navigation_target: '/library?dialog=login',
        });
        navigate('/library?dialog=login');
        return 'login_redirect';
      }

      setCheckoutError(null);
      setActiveCheckoutPriceId(product.stripePriceId);

      if (savedPaymentMethodId) {
        try {
          const response = await quickPurchase(
            product.stripePriceId,
            savedPaymentMethodId
          );

          if (response.success && response.data?.status === 'succeeded') {
            if (successResetTimerRef.current !== null) {
              window.clearTimeout(successResetTimerRef.current);
            }

            setPurchaseSuccessPriceId(product.stripePriceId);
            setActiveCheckoutPriceId(null);
            void refetchUser();
            toast.success('Purchase successful! Your Loons have been added.');
            onQuickPurchaseSuccess?.();

            analyticsService.trackQuickPurchaseSucceeded({
              ...baseAnalyticsParams,
              flow_type: 'quick_purchase',
              checkout_result: 'succeeded',
            });
            analyticsService.trackPurchaseCompleted({
              ...baseAnalyticsParams,
              flow_type: 'quick_purchase',
              checkout_result: 'succeeded',
            });

            successResetTimerRef.current = window.setTimeout(() => {
              setPurchaseSuccessPriceId(null);
            }, quickPurchaseSuccessDurationMs);

            return 'succeeded';
          }

          if (!response.success) {
            analyticsService.trackCheckoutFailed({
              ...baseAnalyticsParams,
              flow_type: 'quick_purchase',
              checkout_result: 'error',
              error_code: 'quick_purchase_request_failed',
              error_message: 'Quick purchase request failed',
            });
          } else if (response.data?.status !== 'fallback') {
            analyticsService.trackCheckoutFailed({
              ...baseAnalyticsParams,
              flow_type: 'quick_purchase',
              checkout_result: 'error',
              error_code: 'quick_purchase_unknown_status',
              error_message: `Quick purchase returned unexpected status '${response.data?.status}'`,
            });
          }

          return await redirectToCheckout({
            stripePriceId: product.stripePriceId,
            cancelPath: cancelPath ?? location.pathname + location.hash,
            analyticsParams: baseAnalyticsParams,
            flowType:
              response.success && response.data?.status === 'fallback'
                ? 'quick_purchase_fallback'
                : 'quick_purchase_exception',
          });
        } catch (quickPurchaseError) {
          logger.error(
            'Quick purchase failed, falling back to checkout redirect',
            quickPurchaseError
          );
          analyticsService.trackCheckoutFailed({
            ...baseAnalyticsParams,
            flow_type: 'quick_purchase',
            checkout_result: 'error',
            error_code: 'quick_purchase_exception',
            error_message: getErrorMessage(quickPurchaseError),
          });
        }
      }

      return await redirectToCheckout({
        stripePriceId: product.stripePriceId,
        cancelPath: cancelPath ?? location.pathname + location.hash,
        analyticsParams: baseAnalyticsParams,
        flowType: savedPaymentMethodId
          ? 'quick_purchase_exception'
          : 'stripe_checkout',
      });
    },
    [
      location.hash,
      location.pathname,
      navigate,
      redirectToCheckout,
      refetchUser,
      user,
    ]
  );

  return {
    checkoutProduct,
    activeCheckoutPriceId,
    purchaseSuccessPriceId,
    checkoutError,
    clearCheckoutError,
  };
}
